/**
 * Service layer — Submittal Review Tool markup + checklist runtime (Submittal Review Plan Phase 5).
 * Wraps /api/submittal-review. See that file's header for the full action list and freeze-guard
 * contract (a posted round's writes are rejected with 409).
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/submittal-review' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(data.error || 'Submittal review request failed: ' + res.status)
    err.status = res.status
    throw err
  }
  return data
}

/** { markup: { [fileId]: annotationJson }, checklist: [ item, ... ] } for one revision. */
export function getSubmittalReviewState(revisionId) {
  if (!revisionId) return Promise.resolve({ markup: {}, checklist: [] })
  return call('GET', null, 'revisionId=' + encodeURIComponent(revisionId))
}

/** Save (upsert) the annotation layer for one package file on one round. Throws (status 409) if
 *  that round's review has already been posted. */
export function saveSubmittalMarkup(revisionId, fileId, annotationJson) {
  return call('POST', { action: 'saveMarkup', revisionId, fileId, annotationJson }).then(d => d.ok)
}

/** Seed (or, if already seeded, just return) this round's checklist state from the given
 *  templates — idempotent. Also carries forward any `flagged` items from the parent round. */
export function seedSubmittalChecklist(revisionId, templateIds) {
  return call('POST', { action: 'seedChecklist', revisionId, templateIds }).then(d => d.checklist)
}

export function setSubmittalChecklistItemStatus(stateId, status, memberId, memberName) {
  return call('POST', { action: 'setChecklistItemStatus', stateId, status, memberId, memberName }).then(d => d.item)
}

export function setSubmittalChecklistItemNote(stateId, note) {
  return call('POST', { action: 'setChecklistItemNote', stateId, note }).then(d => d.item)
}

export function addSubmittalChecklistNote(revisionId, text, memberId, memberName) {
  return call('POST', { action: 'addFreeNote', revisionId, text, memberId, memberName }).then(d => d.item)
}

export function deleteSubmittalChecklistItem(stateId) {
  return call('POST', { action: 'deleteChecklistItem', stateId }).then(d => d.ok)
}
